import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import {
  CLEAR_CART,
  CREATE_ORDER,
  EDIT_PROFILE,
  SET_ADDRESS,
} from '@/lib/graphql/mutations/customer';
import {
  ADDRESS,
  CART_ITEMS,
  ORDER_DETAIL,
  OREDER_POLL,
} from '@/lib/graphql/queries/customer';
import { GET_CITIES, GET_DISTRICTS } from '@/lib/graphql/queries/location';
import { Switch } from '@/components/ui/switch';
import { useMutation, useQuery } from '@apollo/client';
import { Loader2, QrCode } from 'lucide-react';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/use-auth';
import { useIsMobile } from '../hooks/use-mobile';
import { fileUrl } from '../lib/utils';

const duuregShort = (duureg: string) => {
  switch (duureg) {
    case 'Баянгол':
      return 'БГД.';
    case 'Чингэлтэй':
      return 'ЧГД';
    case 'Хан-Уул':
      return 'ХУД';
    case 'Баянзүрх':
      return 'БЗД';
    case 'Сүхбаатар':
      return 'СБД';
    case 'Сонгинохайрхан':
      return 'СХД';
    default:
      return duureg;
  }
};

export default function Checkout() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const { data: cartData, loading: cartLoading } = useQuery(CART_ITEMS);
  const { data: citiesData } = useQuery(GET_CITIES);
  const [clearCart] = useMutation(CLEAR_CART);
  const [qrImage, setQrImage] = useState('');
  const [orderId, setOrderId] = useState('');
  const [deliveryMethod, setDeliveryMethod] = useState<'delivery' | 'pickup'>(
    'delivery',
  );

  const [isWaitingPayment, setIsWaitingPayment] = useState(false);

  const [paymentMethod, setPaymentMethod] = useState<'qpay' | 'lendmn'>('qpay');
  const [lendmnPhone, setLendmnPhone] = useState('');
  const [hasVat, setHasVat] = useState(false);
  const [registerNumber, setRegisterNumber] = useState(
    user?.registerNumber || '',
  );

  // Load VAT preference from localStorage on mount
  React.useEffect(() => {
    if (user) {
      const stored = localStorage.getItem(`vat_config_${user.id}`);
      if (stored) {
        try {
          const config = JSON.parse(stored);
          setHasVat(Boolean(config.hasVat));
        } catch (e) {
          console.error('Failed to parse VAT config', e);
        }
      }
    }
  }, [user]);

  // Persist VAT preference changes to localStorage
  const handleHasVatChange = (value: boolean) => {
    setHasVat(value);
    if (user) {
      localStorage.setItem(
        `vat_config_${user.id}`,
        JSON.stringify({ hasVat: value, userId: user.id })
      );
    }
  };

  // get orderId from query params
  const orderIdParam = new URLSearchParams(window.location.search).get(
    'orderId',
  );

  const { data: pendingOrderData } = useQuery(ORDER_DETAIL, {
    variables: {
      customerOrderId: orderIdParam,
    },
    fetchPolicy: 'network-only',
    skip: !orderIdParam,
    onCompleted: (data) => {
      if (data?.customerOrder) {
        setQrImage(data.customerOrder.paymentData.qrData);
        setOrderId(data.customerOrder.id);
      }
    },
  });

  const [cityId, setCityId] = useState(user?.mainAddress?.cityId || '');
  const [districtId, setDistrictId] = useState(
    user?.mainAddress?.districtId || '',
  );
  const [quarterId, setQuarterId] = useState(
    user?.mainAddress?.quarterId || '',
  );

  const { data: districtsData } = useQuery(GET_DISTRICTS, {
    variables: { cityId },
    skip: !cityId,
  });

  const { data: orderData } = useQuery(OREDER_POLL, {
    variables: {
      id: orderId,
    },
    pollInterval: 10000,
    skip: !orderId,
  });

  React.useEffect(() => {
    if (orderData?.customerOrder?.paymentStatus === 'fully-paid') {
      setIsWaitingPayment(false);
      toast({
        title: 'Амжилттай',
        description: 'Таны захиалга амжилттай хийгдлээ',
      });

      setTimeout(() => {
        navigate(`/orders`);
      }, 1500);
    }
  }, [navigate, orderData, orderId, toast]);

  const [formData, setFormData] = useState({
    cityId: user?.mainAddress?.cityId || '',
    districtId: user?.mainAddress?.districtId || '',
    quarterId: user?.mainAddress?.quarterId || '',
    buildingNumber: user?.mainAddress?.buildingNumber || '',
    floor: user?.mainAddress?.floor || '',
    doorNumber: user?.mainAddress?.doorNumber || '',
    entrance: user?.mainAddress?.entrance || '',
    code: user?.mainAddress?.code || '',
    note: user?.mainAddress?.note || '',
  });

  const prepareAddress = (values: any) => {
    if (deliveryMethod === 'pickup') {
      return `өөрөө ирж авах ${user?.phone}`;
    }

    const city = citiesData?.cities.find(
      (city: any) => city.id === values.cityId,
    );
    const district = districtsData?.districts.find(
      (district: any) => district.id === values.districtId,
    );

    let deliveryAddress = '';

    if (city.name !== 'Улаанбаатар') {
      deliveryAddress =
        city.name +
          ', ' +
          district.name +
          ', Баг: ' +
          values.quarterId +
          ', Байр/Хотхон: ' +
          values.buildingNumber +
          ', Давхар: ' +
          values.floor +
          ', Тоот: ' +
          values.doorNumber +
          ', ' +
          values.notes || '';
    } else {
      deliveryAddress =
        duuregShort(district.name) +
          ', ' +
          values.quarterId +
          'р хороо, Байр/Хотхон: ' +
          values.buildingNumber +
          ', Давхар: ' +
          values.floor +
          ', Тоот: ' +
          values.doorNumber +
          ', ' +
          values.notes || '';
    }

    // remove undefined values
    deliveryAddress = deliveryAddress.replace(/undefined/g, '');

    // remove trailing comma
    deliveryAddress = deliveryAddress.replace(/,\s*$/, '');
    // remove leading comma
    deliveryAddress = deliveryAddress.replace(/^\s*,\s*/, '');

    if (values.phone) {
      deliveryAddress += ', ' + values.phone;
    } else {
      deliveryAddress += ', ' + user?.phone;
    }

    return deliveryAddress;
  };

  const [setAddress] = useMutation(SET_ADDRESS);
  const [createOrder] = useMutation(CREATE_ORDER);
  const [editProfile] = useMutation(EDIT_PROFILE, {
    refetchQueries: ['Profile'],
  });


  const getFinalPrice = (item: any) => {
    let price = item.product.price;

    if (hasVat) {
      if (item.product.isSalonExclusive) {
        const percent = item.product.salonDiscountPercent ?? 30;
        const vatBase = item.product.price / (1 - percent / 100);
        const vatAmount = (vatBase * 10) / 100;
        price += vatAmount;
      } else {
        const vatAmount = (price * 10) / 100;
        price += vatAmount;
      }
    }
    return price;
  }

  const cartItems =
    cartData?.cartItems.filter((item: any) => item.product !== null) || [];

  // let total = cartItems.reduce((sum, item) => {
  //   const price = item.product?.discountPrice || item.product.price;
  //   // if (item.product.baseDiscount) {
  //   //   price = price * (1 - item.product.baseDiscount / 100);
  //   // }
  //   return sum + price * item.quantity;
  // }, 0);

  let total = 0;
  let vatFee = 0;

  for (const item of cartItems) {
    const price = getFinalPrice(item);
    total += price * item.quantity;
    if (hasVat) {
      const vatAmount = price - item.product.price;
      vatFee += vatAmount * item.quantity;
    }
  }


  const lendmnFee = paymentMethod === 'lendmn' ? total * 0.039 : 0;
  const finalTotal =
    total + lendmnFee + (deliveryMethod === 'delivery' ? 7000 : 0);

  const handleSubmit = async (e: React.FormEvent) => {
    setIsWaitingPayment(true);
    e.preventDefault();

    if (deliveryMethod === 'delivery') {
      if (!cityId || !districtId || !quarterId) {
        setIsWaitingPayment(false);
        toast({
          title: 'Алдаа',
          description: 'Бүх хаягaa оруулна уу',
          variant: 'destructive',
        });
        return;
      }
    }

    if (hasVat && !registerNumber.trim()) {
      setIsWaitingPayment(false);
      toast({
        title: 'Алдаа',
        description: 'Регистрийн дугаараа оруулна уу',
        variant: 'destructive',
      });
      return;
    }

    try {
      if (hasVat && !user?.registerNumber && registerNumber.trim()) {
        await editProfile({
          variables: {
            input: {
              registerNumber: registerNumber.trim(),
            },
          },
        });
      }

      if (deliveryMethod === 'delivery') {
        await setAddress({
          variables: {
            input: {
              cityId: formData.cityId,
              districtId: formData.districtId,
              quarterId: formData.quarterId,
              buildingNumber: formData.buildingNumber,
              floor: Number(formData.floor),
              doorNumber: formData.doorNumber,
              entrance: formData.entrance,
              code: formData.code,
              note: formData.note,
            },
          },
        });
      }

      const items = cartItems.map((item) => ({
        variantId: item.variant?.id,
        productId: item.productId,
        quantity: item.quantity,
        price: item.product.hasVariants
          ? item.variant.price
          : item.product.basePrice,
      }));

      const { data } = await createOrder({
        variables: {
          input: {
            pickup: deliveryMethod === 'pickup',
            customerId: Number(user.id),
            deliveryAddress: prepareAddress(formData),
            description: `${formData.note} ${user?.phone}`,
            items,
            paymentMethod: paymentMethod,
            has_vat: hasVat,
            registerNumber: hasVat ? registerNumber.trim() : undefined,
            phoneNumber:
              paymentMethod === 'lendmn' ? lendmnPhone : user?.phone || '',
            // Add any additional order input fields here
          },
        },
      });

      if (data?.customerAddOrder) {
        setQrImage(data.customerAddOrder.paymentData.qrData);
        setOrderId(data.customerAddOrder.id);

        // Clear cart after successful order
        // await clearCart();

        toast({
          title: 'Амжилттай',
          description: `Захиалга #${data.customerAddOrder.orderNumber} амжилттай бүртгэгдлээ.`,
        });
      }

      toast({ title: 'Захиалга амжилттай бүртгэгдлээ!' });
    } catch (error) {
      setIsWaitingPayment(false);
      console.error(error);
      toast({
        title: 'Захиалга үүсгэхэд алдаа гарлаа',
        variant: 'destructive',
      });
    }
  };

  React.useEffect(() => {
    if (user?.mainAddress) {
      setFormData((prev) => ({
        ...prev,
        cityId: user.mainAddress.cityId,
        districtId: user.mainAddress.districtId,
        quarterId: user.mainAddress.quarterId,
        buildingNumber: user.mainAddress.buildingNumber,
        floor: user.mainAddress.floor,
        doorNumber: user.mainAddress.doorNumber,
        entrance: user.mainAddress.entrance,
        code: user.mainAddress.code,
        note: user.mainAddress.note,
      }));

      const address = user.mainAddress;

      if (address.cityId) {
        setCityId(address.cityId);
      }
      if (address.districtId) {
        setDistrictId(address.districtId);
      }
      if (address.quarterId) {
        setQuarterId(address.quarterId);
      }
    }
  }, [user]);

  React.useEffect(() => {
    if (user?.registerNumber) {
      setRegisterNumber(user.registerNumber);
    }
  }, [user?.registerNumber]);

  if (cartLoading) {
    return <div className='container mx-auto px-4 py-8'>Ачааллаж байна...</div>;
  }

  if (isWaitingPayment && paymentMethod === 'lendmn') {
    return (
      <div className='min-h-screen flex flex-col'>
        {/* <Navbar /> */}
        <main className='flex-grow pt-20 pb-10'>
          <div className='container-custom'>
            <div className='max-w-2xl mx-auto bg-white p-8 rounded-lg border shadow-sm'>
              <div className='text-center'>
                <h1 className='text-2xl font-bold mb-2'>Төлбөр шалгаж байна</h1>
                <p className='text-muted-foreground mb-6'>
                  Та lendmn аппликейшнээрээ дамжуулан төлбөрөө төлнө үү.
                </p>
              </div>

              <div className='relative flex justify-center items-center mt-8 mb-4'>
                <div className='flex justify-center items-center py-20'>
                  <Loader2 className='h-8 w-8 text-gray-400 animate-spin mx-auto mb-4' />
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Show QR code if order is created or if loading a pending order
  if (qrImage) {
    return (
      <div className='min-h-screen flex flex-col'>
        <main className='flex-grow pt-20 pb-10'>
          <div className='container-custom'>
            <div className='max-w-2xl mx-auto bg-white p-8 rounded-lg border shadow-sm'>
              <div className='text-center'>
                <QrCode className='w-12 h-12 mx-auto mb-4 text-primary' />
                <h1 className='text-2xl font-bold mb-2'>Төлбөр төлөх QR код</h1>
                <p className='text-muted-foreground mb-4'>
                  Банкны аппликейшн ашиглан QR кодыг уншуулна уу
                </p>
                <p className='text-sm text-amber-600 font-medium mb-6 bg-amber-50 p-3 rounded-lg'>
                  ⚠️ Төлбөр амжилттай төлөгдөж хуудас автоматаар шинэчлэгдэх
                  хүртэл веб хөтөчөө битгий хаагаарай
                </p>
                {!isMobile && (
                  <div className='mb-6 flex justify-center'>
                    <img
                      src={qrImage}
                      alt='Payment QR Code'
                      className='max-w-full h-auto border rounded-lg'
                      style={{ maxHeight: '300px' }}
                    />
                  </div>
                )}

                {isMobile &&
                  orderData?.customerOrder?.paymentData?.urls?.length > 0 && (
                    <div className='mt-6'>
                      <h3 className='text-lg font-semibold mb-4'>
                        Банкны аппликейшн нээх
                      </h3>
                      <div className='grid grid-cols-3 sm:grid-cols-4 gap-3'>
                        {orderData.customerOrder.paymentData.urls.map(
                          (bank: any) => (
                            <Button
                              key={bank.name}
                              onClick={() => window.open(bank.link, '_blank')}
                              className='flex flex-col items-center justify-center px-2 py-3 h-28 overflow-hidden text-ellipsis text-center whitespace-nowrap'
                              variant='outline'
                            >
                              <img
                                src={bank.logo}
                                alt={bank.name}
                                className='w-10 h-10 object-contain mb-2'
                              />
                              <span className='text-xs text-center leading-tight break-words w-full max-w-[80px]'>
                                {bank.name}
                              </span>
                            </Button>
                          ),
                        )}
                      </div>
                    </div>
                  )}

                <p className='text-sm text-muted-foreground mb-6'>
                  Төлбөр төлөгдсөнөөр таны захиалга баталгаажина.
                </p>

                <Button
                  onClick={() => navigate('/orders')}
                  className='w-full md:w-auto'
                >
                  Захиалга харах
                </Button>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className='container mx-auto px-4 py-8'>
      <h1 className='text-3xl font-bold mb-8'>Захиалга</h1>

      <div className='grid lg:grid-cols-3 gap-8'>
        <div className='lg:col-span-2'>
          <form onSubmit={handleSubmit} className='space-y-6'>
            {/* Delivery Method Selection */}
            <div className='border rounded-lg p-6 bg-card'>
              <h2 className='text-xl font-semibold mb-4'></h2>
              <RadioGroup
                value={deliveryMethod}
                onValueChange={(value) =>
                  setDeliveryMethod(value as 'delivery' | 'pickup')
                }
              >
                <div className='flex items-center space-x-2 mb-3'>
                  <RadioGroupItem value='delivery' id='delivery' />
                  <Label
                    htmlFor='delivery'
                    className='cursor-pointer font-normal'
                  >
                    Хүргэлт
                  </Label>
                </div>
                <div className='flex items-center space-x-2'>
                  <RadioGroupItem value='pickup' id='pickup' />
                  <Label
                    htmlFor='pickup'
                    className='cursor-pointer font-normal'
                  >
                    Өөрөө авах
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {/* Payment Method Selection */}
            <div className='border rounded-lg p-6 bg-card'>
              <h2 className='text-xl font-semibold mb-4'>Төлбөрийн хэрэгсэл</h2>
              <div className='flex flex-row gap-4'>
                <button
                  type='button'
                  onClick={() => setPaymentMethod('qpay')}
                  className={`flex-1 border-2 rounded-lg p-3 transition-all ${
                    paymentMethod === 'qpay'
                      ? 'border-primary bg-primary/5'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <img
                    src='/qpay.png'
                    alt='QPay'
                    className='w-full h-12 object-contain'
                  />
                </button>
                <button
                  type='button'
                  onClick={() => setPaymentMethod('lendmn')}
                  className={`flex-1 border-2 rounded-lg p-3 transition-all ${
                    paymentMethod === 'lendmn'
                      ? 'border-primary bg-primary/5'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <img
                    src='/lendmn.png'
                    alt='LendMN'
                    className='w-full h-12 object-contain mb-2'
                  />
                  <p className='text-xs text-center text-muted-foreground'>
                    30 хоногт хүссэнээрээ хуваан төлөх боломжтой
                  </p>
                </button>
              </div>

              {/* LendMN Phone Input */}
              {paymentMethod === 'lendmn' && (
                <div className='mt-4 space-y-2'>
                  <Label htmlFor='lendmnPhone'>
                    Lendmn аппликейшнд бүртгэлтэй утасны дугаараа оруулна уу
                  </Label>
                  <Input
                    id='lendmnPhone'
                    type='tel'
                    placeholder='Утасны дугаар'
                    value={lendmnPhone}
                    onChange={(e) => setLendmnPhone(e.target.value)}
                  />
                </div>
              )}

              <div className='mt-6 pt-4 border-t space-y-4'>
                <div className='flex items-center justify-between'>
                  <Label
                    htmlFor='hasVat'
                    className='font-medium cursor-pointer'
                  >
                    НӨАТ авах
                  </Label>
                  <Switch
                    id='hasVat'
                    checked={hasVat}
                    onCheckedChange={handleHasVatChange}
                  />
                </div>

                {hasVat && (
                  <div className='space-y-2'>
                    <Label htmlFor='registerNumber'>Регистрийн дугаар</Label>
                    <Input
                      id='registerNumber'
                      placeholder='Регистрийн дугаар'
                      value={registerNumber}
                      onChange={(e) =>
                        setRegisterNumber(e.target.value.toUpperCase())
                      }
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Delivery Address Form - Only shown when delivery is selected */}
            {deliveryMethod === 'delivery' && (
              <div className='border rounded-lg p-6 bg-card'>
                <h2 className='text-xl font-semibold mb-4'>Хүргэлтийн хаяг</h2>
                <div className='grid md:grid-cols-2 gap-4'>
                  <div className='space-y-2'>
                    <Label htmlFor='city'>Хот/Аймаг</Label>
                    <Select
                      value={cityId}
                      onValueChange={(cityId) => {
                        setCityId(cityId);
                        setFormData({
                          ...formData,
                          cityId: cityId,
                        });
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder='Хот/Аймаг' />
                      </SelectTrigger>
                      <SelectContent>
                        {citiesData?.cities?.map((city) => (
                          <SelectItem key={city.id} value={city.id}>
                            {city.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className='space-y-2'>
                    <Label htmlFor='district'>Дүүрэг/Сум</Label>
                    <Select
                      value={districtId}
                      onValueChange={(districtId) => {
                        setDistrictId(districtId);
                        setFormData({
                          ...formData,
                          districtId: districtId,
                        });
                      }}
                      disabled={!cityId}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder='Дүүрэг/Сум' />
                      </SelectTrigger>
                      <SelectContent>
                        {districtsData?.districts?.map((district) => (
                          <SelectItem key={district.id} value={district.id}>
                            {district.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className='space-y-2'>
                    <Label htmlFor='quarterId'>Хороо/Баг</Label>
                    <Input
                      id='quarterId'
                      placeholder='Хороо/Баг'
                      value={formData.quarterId}
                      onChange={(e) => {
                        const value = e.target.value;
                        setQuarterId(value);
                        setFormData({
                          ...formData,
                          quarterId: value,
                        });
                      }}
                    />
                  </div>

                  <div className='space-y-2'>
                    <Label htmlFor='buildingNumber'>Байр</Label>
                    <Input
                      id='buildingNumber'
                      placeholder='Байр'
                      value={formData.buildingNumber}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          buildingNumber: e.target.value,
                        })
                      }
                    />
                  </div>

                  <div className='space-y-2'>
                    <Label htmlFor='entrance'>Орц/Гудамж</Label>
                    <Input
                      id='entrance'
                      placeholder='Орц/Гудамж'
                      value={formData.entrance}
                      onChange={(e) =>
                        setFormData({ ...formData, entrance: e.target.value })
                      }
                    />
                  </div>

                  <div className='space-y-2'>
                    <Label htmlFor='floor'>Давхар</Label>
                    <Input
                      id='floor'
                      placeholder='Давхар'
                      value={formData.floor}
                      onChange={(e) =>
                        setFormData({ ...formData, floor: e.target.value })
                      }
                    />
                  </div>

                  <div className='space-y-2'>
                    <Label htmlFor='doorNumber'>Тоот</Label>
                    <Input
                      id='doorNumber'
                      placeholder='Тоот'
                      value={formData.doorNumber}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          doorNumber: e.target.value,
                        })
                      }
                    />
                  </div>

                  <div className='space-y-2 md:col-span-2'>
                    <Label htmlFor='note'>Нэмэлт тэмдэглэл</Label>
                    <Textarea
                      id='note'
                      value={formData.note}
                      onChange={(e) =>
                        setFormData({ ...formData, note: e.target.value })
                      }
                      placeholder='Орцны код гэх мэт...'
                    />
                  </div>
                </div>
              </div>
            )}
          </form>
        </div>

        <div className='lg:col-span-1'>
          {/* Order Summary Card – Sticky on desktop */}
          <div className='border rounded-lg p-6 bg-card sticky top-24'>
            <h2 className='text-xl font-bold mb-6'>Таны захиалга</h2>

            <div className='space-y-4 mb-6 max-h-96 overflow-y-auto'>
              {cartItems.map((item: any) => {
                const imageUrl = item.product.thumbnail || '';
                const price = getFinalPrice(item);

                return (
                  <div
                    key={item.id}
                    className='flex gap-4 pb-4 border-b last:border-0'
                  >
                    <img
                      src={fileUrl(imageUrl)}
                      alt={item.product.name}
                      className='w-16 h-16 object-cover rounded-lg flex-shrink-0'
                    />
                    <div className='flex-1'>
                      <h4 className='font-medium text-sm line-clamp-2'>
                        {item.product.name}
                      </h4>
                      <p className='text-sm text-muted-foreground mt-1'>
                        {item.quantity} × {price.toLocaleString('mn-MN')}₮
                      </p>
                    </div>
                    <p className='font-semibold text-right'>
                      {hasVat ? 'НӨАТ-тэй үнэ' : ''}{' '}
                      {(price * item.quantity).toLocaleString('mn-MN')}₮
                    </p>
                  </div>
                );
              })}
            </div>

            {/* Delivery Fee */}
            {deliveryMethod === 'delivery' && (
              <div className='flex justify-between py-3 border-t'>
                <span>Хүргэлтийн төлбөр</span>
                <span className='font-medium'>7,000₮</span>
              </div>
            )}

            {/* LendMN Fee */}
            {hasVat && (
              <div className='flex justify-between py-3 border-t'>
                <span>НӨАТ (10%)</span>
                <span className='font-medium'>
                  {vatFee.toLocaleString('mn-MN')}₮
                </span>
              </div>
            )}

            {paymentMethod === 'lendmn' && (
              <div className='flex justify-between py-3 border-t'>
                <span>LendMN шимтгэл (3.9%)</span>
                <span className='font-medium'>
                  {lendmnFee.toLocaleString('mn-MN')}₮
                </span>
              </div>
            )}

            {/* Total */}
            <div className='border-t-2 border-primary pt-4 mt-4'>
              <div className='flex justify-between text-xl font-bold'>
                <span>Нийт дүн</span>
                <span className='text-primary'>
                  {finalTotal.toLocaleString('mn-MN')}₮
                </span>
              </div>
            </div>

            {/* PAYMENT BUTTON MOVED HERE – Perfect placement! */}
            <Button
              type='submit'
              size='lg'
              className='w-full mt-6 text-lg font-semibold h-14'
              onClick={handleSubmit} // or form onSubmit will catch it
              disabled={
                cartItems.length === 0 ||
                (paymentMethod === 'lendmn' && lendmnPhone.length < 8) ||
                (hasVat && registerNumber.trim().length === 0)
              }
            >
              Төлбөр төлөх
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
